import React, { useState, useEffect } from 'react';
import { Form, Button, Container, Row, Col, Card, Alert } from 'react-bootstrap';
import { useSearchParams, useNavigate } from 'react-router-dom';
import './Login.css'; // Reuse the same CSS styles as Login

const PasswordResetConfirm = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [passwordsMatch, setPasswordsMatch] = useState(true);

  useEffect(() => {
    // Get token from URL parameters
    const tokenParam = searchParams.get('token');
    if (tokenParam) {
      setToken(tokenParam);
    } else {
      setError('Invalid or missing reset token. Please request a new password reset.');
    }
  }, [searchParams]);

  useEffect(() => {
    // Check if passwords match when either field changes
    if (confirmPassword) {
      setPasswordsMatch(newPassword === confirmPassword);
    } else {
      setPasswordsMatch(true); // Don't show error when confirm field is empty
    }
  }, [newPassword, confirmPassword]);

  const validatePassword = (password) => {
    return password.length >= 6;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!token) {
      setError('Invalid reset token');
      return;
    }

    if (!validatePassword(newPassword)) {
      setError('Password must be at least 6 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    setError('');
    setMessage('');

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/users/confirm-reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          token: token,
          newPassword: newPassword 
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setMessage(data.message || 'Password has been successfully reset.');
        
        // Redirect to login page after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else if (response.status === 400) {
        const errorData = await response.json();
        setError(errorData.message || 'Invalid or expired reset token.');
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } catch (err) {
      console.error('Password reset error:', err);
      setError('Something went wrong while connecting. Please try again in a few moments.');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  return (
    <Container fluid className="login-container">
      <Row className="w-100 h-100">
        <Col className="d-flex justify-content-center align-items-center">
          <Card className="login-card">
            <Card.Body className="text-center">
              <img src="images/logo.png" alt="SkedGuy" className="mb-3" style={{ width: '100%' }}/>
              <h4 className="mb-3">Reset Your Password</h4>
              
              {message && (
                <Alert variant="success" className="mb-3">
                  {message}
                  <div className="mt-2 small">
                    Redirecting to login page in 3 seconds...
                  </div>
                </Alert>
              )}
              
              {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
              
              {!message && (
                <>
                  <p className="text-muted mb-4">
                    Enter your new password below
                  </p>
                  
                  <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-3" controlId="newPassword">
                      <Form.Control
                        type="password"
                        placeholder="New Password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        required
                        minLength={6}
                        className={newPassword && !validatePassword(newPassword) ? 'is-invalid' : ''}
                      />
                      <Form.Control.Feedback type="invalid">
                        Password must be at least 6 characters long.
                      </Form.Control.Feedback>
                    </Form.Group>
                    
                    <Form.Group className="mb-3" controlId="confirmPassword">
                      <Form.Control
                        type="password"
                        placeholder="Confirm New Password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        className={!passwordsMatch ? 'is-invalid' : ''}
                      />
                      <Form.Control.Feedback type="invalid">
                        Passwords do not match.
                      </Form.Control.Feedback>
                    </Form.Group>
                    
                    {/* Password strength indicator */}
                    {newPassword && (
                      <div className="mb-3">
                        <small className={`text-${validatePassword(newPassword) ? 'success' : 'danger'}`}>
                          {validatePassword(newPassword) ? '✓ Password meets requirements' : '✗ Password too short (minimum 6 characters)'}
                        </small>
                      </div>
                    )}
                    
                    <Button
                      className="login-button w-100 mb-3"
                      type="submit"
                      disabled={loading || !token || !newPassword || !confirmPassword || !passwordsMatch || !validatePassword(newPassword)}
                    >
                      {loading ? 'Resetting Password...' : 'Reset Password'}
                    </Button>
                  </Form>
                </>
              )}
              
              <Button
                variant="link"
                className="mt-2"
                onClick={handleBackToLogin}
                disabled={loading}
              >
                Back to Login
              </Button>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default PasswordResetConfirm;